import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { thinkingFrameworkService, ThinkingFrameworkSession } from '../../services/thinkingFrameworkService';
import PageHeader from '../common/PageHeader';
import FrameworkGuide from '../common/FrameworkGuide';
import './ThinkingFrameworks.css';

interface SecondOrderThinkingProps {
  onToggleSidebar: () => void;
}

interface SecondOrderData {
  decision: string;
  immediateConsequences: string[]; // 1st Order
  secondOrderConsequences: string[]; // 2nd Order
  thirdOrderConsequences: string[]; // 3rd Order
  assessment: string; // Final verdict
}

const SecondOrderGuideContent = (
  <div>
    <h3>What is Second-Order Thinking?</h3>
    <p>Second-order thinking is the practice of tracing the consequences of a decision further than the immediate effect. It asks "And then what?" to uncover long-term outcomes.</p>
    
    <h3>How to use it</h3>
    <ol>
      <li><strong>The Decision:</strong> What action are you proposing?</li>
      <li><strong>1st Order Consequences:</strong> What happens immediately? (Usually the intended positive outcome).</li>
      <li><strong>2nd Order Consequences:</strong> "And then what?" What happens as a result of the 1st order consequences? (Often negative or unintended).</li>
      <li><strong>3rd Order Consequences:</strong> What happens next? Looking further down the timeline.</li>
      <li><strong>Assessment:</strong> Is the short-term gain worth the long-term pain? Or vice versa?</li>
    </ol>

    <h3>Example</h3>
    <div className="framework-guide-example">
      <p><strong>Decision:</strong> Eat a donut.</p>
      <p><strong>1st Order:</strong> It tastes good. I get a sugar rush.</p>
      <p><strong>2nd Order:</strong> Blood sugar crash later. I feel hungry again soon.</p>
      <p><strong>3rd Order:</strong> Weight gain over time. Poor health.</p>
    </div>
  </div>
);

const SecondOrderThinking: React.FC<SecondOrderThinkingProps> = ({ onToggleSidebar }) => {
  const { sessionId } = useParams<{ sessionId?: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [session, setSession] = useState<ThinkingFrameworkSession | null>(null);
  const [title, setTitle] = useState('');
  const [data, setData] = useState<SecondOrderData>({
    decision: '',
    immediateConsequences: [],
    secondOrderConsequences: [],
    thirdOrderConsequences: [],
    assessment: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [autoSaveTimer, setAutoSaveTimer] = useState<NodeJS.Timeout | null>(null);
  const [showGuide, setShowGuide] = useState(false);

  useEffect(() => {
    if (user) {
      if (sessionId) {
        loadSession();
      } else {
        // New session
        setTitle('');
        setData({
          decision: '',
          immediateConsequences: [],
          secondOrderConsequences: [],
          thirdOrderConsequences: [],
          assessment: ''
        });
        setLoading(false);
        setShowGuide(true);
      }
    }
  }, [user, sessionId]);

  const loadSession = async () => {
    if (!sessionId || !user) return;

    try {
      setLoading(true);
      setError(null);
      const loadedSession = await thinkingFrameworkService.getSession(sessionId);
      setSession(loadedSession);
      setTitle(loadedSession.title);
      
      const loadedData = loadedSession.framework_data as SecondOrderData;
      setData({
        decision: loadedData.decision || '',
        immediateConsequences: loadedData.immediateConsequences || [],
        secondOrderConsequences: loadedData.secondOrderConsequences || [],
        thirdOrderConsequences: loadedData.thirdOrderConsequences || [],
        assessment: loadedData.assessment || ''
      });
    } catch (err) {
      console.error('Error loading session:', err);
      setError('Failed to load session');
    } finally {
      setLoading(false);
    }
  };

  const saveSession = useCallback(async (dataToSave: { title?: string; framework_data?: SecondOrderData }) => {
    if (!user) return;

    try {
      setSaving(true);
      setError(null);

      if (sessionId && session) {
        // Update existing session
        const updated = await thinkingFrameworkService.updateSession(sessionId, dataToSave);
        setSession(updated);
      } else {
        // Create new session
        const newSession = await thinkingFrameworkService.createSession({
          framework_type: 'second_order',
          title: dataToSave.title || 'Untitled 2nd Order Thinking',
          framework_data: dataToSave.framework_data || data
        });
        setSession(newSession);
        navigate(`/thinking-frameworks/second_order/${newSession.session_id}`, { replace: true });
      }
    } catch (err) {
      console.error('Error saving session:', err);
      setError('Failed to save session');
    } finally {
      setSaving(false);
    }
  }, [user, sessionId, session, data, navigate]);

  // Debounced auto-save
  useEffect(() => {
    if (autoSaveTimer) {
      clearTimeout(autoSaveTimer);
    }

    const hasContent = title || data.decision || data.assessment;

    if (hasContent) {
      const timer = setTimeout(() => {
        saveSession({ title, framework_data: data });
      }, 2000); // Auto-save after 2 seconds
      setAutoSaveTimer(timer);
    }

    return () => {
      if (autoSaveTimer) {
        clearTimeout(autoSaveTimer);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, data]);

  const handleTitleChange = (newTitle: string) => {
    setTitle(newTitle);
  };

  const handleDataChange = (field: keyof SecondOrderData, value: string) => {
    setData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleListChange = (field: keyof SecondOrderData, value: string) => {
    if (field === 'decision' || field === 'assessment') return;
    const list = value.split('\n');
    setData(prev => ({
      ...prev,
      [field]: list
    }));
  };

  const handleDelete = async () => {
    if (!sessionId) return;
    if (!window.confirm('Are you sure you want to delete this analysis?')) return;

    try {
      setError(null);
      await thinkingFrameworkService.deleteSession(sessionId);
      navigate('/thinking-frameworks');
    } catch (err) {
      console.error('Error deleting session:', err);
      setError('Failed to delete session');
    }
  };

  const handleBack = () => {
    navigate('/thinking-frameworks');
  };

  if (loading) {
    return (
      <div className="thinking-framework-page">
        <PageHeader
          onToggleSidebar={onToggleSidebar}
          centerContent={<h1 className="thinking-framework-header-title">/second-order</h1>}
        />
        <div className="thinking-framework-content">
          <div className="thinking-framework-loading">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="thinking-framework-page">
      <PageHeader
        onToggleSidebar={onToggleSidebar}
        centerContent={<h1 className="thinking-framework-header-title">/second-order</h1>}
        rightActions={
          <div className="thinking-framework-actions">
            {saving && <span className="thinking-framework-saving">Saving...</span>}
            <button className="framework-guide-btn" onClick={() => setShowGuide(true)}>
              💡 Guide
            </button>
            {sessionId && (
              <button className="thinking-framework-delete-btn" onClick={handleDelete}>
                Delete
              </button>
            )}
            <button className="thinking-framework-back-btn" onClick={handleBack}>
              ← Back
            </button>
          </div>
        }
      />
      
      <div className="thinking-framework-content">
        {error && (
          <div className="thinking-framework-error">{error}</div>
        )}

        <div className="thinking-framework-title-section">
          <input
            type="text"
            value={title}
            onChange={(e) => handleTitleChange(e.target.value)}
            placeholder="Analysis Title..."
            className="thinking-framework-title-input"
          />
        </div>

        <div className="second-order-container">
          <div className="framework-section">
            <h3>The Decision / Action</h3>
            <textarea
              value={data.decision}
              onChange={(e) => handleDataChange('decision', e.target.value)}
              placeholder="What action are you considering?"
              rows={3}
            />
          </div>

          <div className="consequences-flow">
            <div className="framework-section consequence-level level-1">
              <h3>1. Immediate Consequences (1st Order)</h3>
              <p className="framework-hint">What happens right away?</p>
              <textarea
                value={data.immediateConsequences.join('\n')}
                onChange={(e) => handleListChange('immediateConsequences', e.target.value)}
                placeholder="List immediate outcomes..."
                rows={4}
              />
            </div>

            <div className="arrow-down">↓</div>

            <div className="framework-section consequence-level level-2">
              <h3>2. Second Order Consequences</h3>
              <p className="framework-hint">And then what? What results from the 1st order effects?</p>
              <textarea
                value={data.secondOrderConsequences.join('\n')}
                onChange={(e) => handleListChange('secondOrderConsequences', e.target.value)}
                placeholder="List subsequent outcomes..."
                rows={4}
              />
            </div>

            <div className="arrow-down">↓</div>

            <div className="framework-section consequence-level level-3">
              <h3>3. Third Order Consequences (Long Term)</h3>
              <p className="framework-hint">What are the long-term ripple effects?</p>
              <textarea
                value={data.thirdOrderConsequences.join('\n')}
                onChange={(e) => handleListChange('thirdOrderConsequences', e.target.value)}
                placeholder="List long-term outcomes..."
                rows={4}
              />
            </div>
          </div>

          <div className="framework-section assessment-section">
            <h3>Final Assessment</h3>
            <textarea
              value={data.assessment}
              onChange={(e) => handleDataChange('assessment', e.target.value)}
              placeholder="Given the 2nd and 3rd order consequences, is this a good decision?"
              rows={3}
            />
          </div>
        </div>
      </div>

      <FrameworkGuide
        isOpen={showGuide}
        onClose={() => setShowGuide(false)}
        title="Second-Order Thinking"
        content={SecondOrderGuideContent}
      />
    </div>
  );
};

export default SecondOrderThinking;

